import { ElMessage, messageConfig, messageDefaults, messageTypes } from 'element-plus'
import { readDisplayState } from './shared/displayState'

const nativeMethods = Object.fromEntries(messageTypes.map(type => [type, ElMessage[type]]))
let messagePolicy

function isMobileMessageViewport() {
  if (typeof window === 'undefined') return false
  const state = readDisplayState()
  return state.layoutTier === 'compact' || (state.isIPad && !state.hasHover)
}

function isTransientTextMessage(component) {
  const { duration, message, showClose, dangerouslyUseHTMLString } = component.$props
  return Number.isFinite(duration) && duration > 0 && typeof message === 'string' &&
    !showClose && !dangerouslyUseHTMLString && !component.$slots.default &&
    !Object.keys(component.$attrs).some(key => /^on[A-Z]/.test(key))
}

function showMessage(options = {}, context, type) {
  const params = typeof options === 'string' ? { message: options } : options
  const nativeMessage = type ? nativeMethods[type] : ElMessage
  if (!messagePolicy) return nativeMessage(options, context)

  let duration = params && Object.prototype.hasOwnProperty.call(params, 'duration')
    ? params.duration
    : messageDefaults.duration
  if (typeof messageConfig.duration === 'number' && duration === messageDefaults.duration) {
    duration = messageConfig.duration
  }
  if (duration === undefined) duration = messageDefaults.duration
  const transient = params && typeof params.message === 'string' && Number.isFinite(duration) && duration > 0 &&
    !(params.showClose || messageConfig.showClose) && !params.dangerouslyUseHTMLString &&
    !Object.keys(params).some(key => /^on[A-Z]/.test(key) && key !== 'onClose')

  const policy = messagePolicy
  const mobile = isMobileMessageViewport()
  const route = policy.router.currentRoute.value
  const onClose = params?.onClose
  let handler
  let handlerOptions = options
  if (transient) {
    handlerOptions = {
      ...params,
      onClose: (...args) => {
        policy.messages.delete(handler)
        onClose?.(...args)
      }
    }
    // Explicit business durations and close callbacks retain their existing timing.
    if (mobile && params.duration === undefined && duration === messageDefaults.duration && !onClose) {
      handlerOptions.duration = 1500
    }
  }
  handler = nativeMessage(handlerOptions, context)
  // Grouping keeps its original close callback, but a new page may reuse the message.
  const existing = policy.owners.get(handler)
  if (existing) existing.route = route
  else {
    const entry = { mobile, route, onClose }
    // Remember excluded handlers too: grouping must not turn a persistent message transient.
    policy.owners.set(handler, entry)
    if (transient) policy.messages.set(handler, entry)
  }
  return handler
}

function mobileMessage(options, context) {
  return showMessage(options, context)
}

export function installMobileMessageAutoClose(app, router) {
  if (router) {
    const policy = { router, messages: new Map(), owners: new WeakMap() }
    messagePolicy = policy
    Object.assign(mobileMessage, ElMessage)
    for (const type of messageTypes) {
      const typedMessage = (options, context) => showMessage(options, context, type)
      ElMessage[type] = typedMessage
      mobileMessage[type] = typedMessage
    }
    app.config.globalProperties.$message = mobileMessage
    const removeAfterEach = router.afterEach((to, from, failure) => {
      if (failure || to.fullPath === from.fullPath || !isMobileMessageViewport()) return
      // Snapshot before closing: an onClose callback may create another message.
      for (const [handler, entry] of [...policy.messages]) {
        if (entry.mobile && entry.route !== to && !entry.onClose) {
          policy.messages.delete(handler)
          handler.close()
        }
      }
    })
    app.onUnmount(() => {
      removeAfterEach()
      policy.messages.clear()
      if (messagePolicy === policy) messagePolicy = undefined
    })
  }
  const listeners = new WeakMap()
  app.mixin({
    mounted() {
      if (this.$options.name !== 'ElMessage') return
      const element = this.$el
      let suppressHover
      const keepTimer = event => {
        if (event.target !== element) return
        const shouldSuppress = isMobileMessageViewport() && isTransientTextMessage(this)
        if (event.type === 'mouseenter') {
          suppressHover = shouldSuppress
        } else {
          // Pair leave with enter so crossing a breakpoint cannot strand a paused timer.
          const suppressLeave = suppressHover ?? shouldSuppress
          suppressHover = undefined
          if (!suppressLeave) return
        }
        if (event.type === 'mouseenter' && !suppressHover) return
        // Touch compatibility mouse events must not pause or restart the native timer.
        event.stopImmediatePropagation()
      }
      element.addEventListener('mouseenter', keepTimer, true)
      element.addEventListener('mouseleave', keepTimer, true)
      listeners.set(this, () => {
        element.removeEventListener('mouseenter', keepTimer, true)
        element.removeEventListener('mouseleave', keepTimer, true)
      })
    },
    beforeUnmount() {
      listeners.get(this)?.()
      listeners.delete(this)
    }
  })
}
